// @ts-nocheck
/**
 * Individual Learning Session Interface
 * Interactive learning session with progress tracking and adaptive features
 * Epic 75 - Individual Adult Self-Paced Learning
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Column,
  Tile,
  ProgressIndicator,
  ProgressStep,
  Button,
  Toggle,
  Slider,
  TextArea,
  RadioButton,
  RadioButtonGroup,
  Checkbox,
  Modal,
  InlineNotification,
  Loading,
  Breadcrumb,
  BreadcrumbItem,
  Tag,
  ContentSwitcher,
  Switch,
  Accordion,
  AccordionItem,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
} from '@carbon/react';
import {
  PlayFilled,
  PauseFilled,
  SkipForwardFilled,
  SkipBackFilled,
  CheckmarkFilled,
  CloseFilled,
  Help,
  Settings,
  Bookmark,
  Chat,
  VolumeUpFilled,
  VolumeOffFilled,
  Maximize, // Changed from FullScreen
  Minimize, // Changed from FullScreenExit
  TimeFilled,
  InformationFilled,
} from '@carbon/icons-react';

interface LearningContent {
  content_id: string;
  content_type: 'video' | 'text' | 'interactive' | 'quiz' | 'practice';
  title: string;
  description: string;
  duration_minutes: number;
  difficulty_level: number;
  learning_objectives: string[];
  content_data: any;
}

interface SessionProgress {
  session_id: string;
  current_content_index: number;
  completion_percentage: number;
  time_spent_minutes: number;
  concepts_mastered: string[];
  knowledge_gaps: string[];
  engagement_score: number;
}

interface AdaptiveSettings {
  pace_preference: 'slow' | 'normal' | 'fast';
  difficulty_preference: number; // 1-10
  content_format_preference: string[];
  accessibility_settings: {
    font_size: 'small' | 'medium' | 'large';
    high_contrast: boolean;
    audio_enabled: boolean;
    closed_captions: boolean;
  };
}

const IndividualLearningSession: React.FC = () => {
  // State management
  const [sessionData, setSessionData] = useState<any>(null);
  const [contentItems, setContentItems] = useState<LearningContent[]>([]);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [progress, setProgress] = useState<SessionProgress | null>(null);
  const [adaptiveSettings, setAdaptiveSettings] = useState<AdaptiveSettings>({
    pace_preference: 'normal',
    difficulty_preference: 5,
    content_format_preference: ['video', 'text', 'interactive'],
    accessibility_settings: {
      font_size: 'medium',
      high_contrast: false,
      audio_enabled: true,
      closed_captions: false,
    },
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [userResponse, setUserResponse] = useState('');
  const [selfAssessment, setSelfAssessment] = useState({
    difficulty_rating: 5,
    confidence_level: 5,
    satisfaction_score: 5,
    understanding_level: 5,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Load session data on mount
  useEffect(() => {
    loadSessionData();
    startSessionTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const loadSessionData = async () => {
    try {
      setIsLoading(true);

      // Mock session data - in real implementation, load from API
      const mockSession = {
        session_id: 'session-001',
        pathway_id: 'pathway-tech-001',
        course_id: 'intro-programming',
        session_type: 'study',
        learning_objectives: {
          session_goals: ['Understand React hooks', 'Build interactive components', 'Master state management'],
          expected_outcomes: [
            'Create functional components',
            'Implement useState and useEffect',
            'Handle user interactions',
          ],
        },
      };

      const mockContent: LearningContent[] = [
        {
          content_id: 'content-001',
          content_type: 'video',
          title: 'Introduction to React Hooks',
          description: 'Learn the fundamentals of React hooks and why they revolutionized React development',
          duration_minutes: 15,
          difficulty_level: 3,
          learning_objectives: ['Understand hooks concept', 'Know when to use hooks', 'Identify hook benefits'],
          content_data: {
            video_url: 'https://example.com/react-hooks-intro.mp4',
            transcript: 'Video transcript would be here...',
            interactive_elements: [],
          },
        },
        {
          content_id: 'content-002',
          content_type: 'interactive',
          title: 'useState Hook Practice',
          description: 'Interactive coding exercise to practice useState hook',
          duration_minutes: 20,
          difficulty_level: 4,
          learning_objectives: ['Use useState effectively', 'Manage component state', 'Handle state updates'],
          content_data: {
            exercise_type: 'code_completion',
            starter_code: 'const [count, setCount] = useState(0);',
            expected_solution: 'Complete counter component implementation',
            hints: ['Remember to import useState', 'Use setCount to update state', 'Display count in JSX'],
          },
        },
        {
          content_id: 'content-003',
          content_type: 'quiz',
          title: 'React Hooks Knowledge Check',
          description: 'Test your understanding of React hooks concepts',
          duration_minutes: 10,
          difficulty_level: 3,
          learning_objectives: ['Validate understanding', 'Identify knowledge gaps', 'Reinforce key concepts'],
          content_data: {
            questions: [
              {
                question: 'Which hook is used for managing component state?',
                options: ['useEffect', 'useState', 'useContext', 'useReducer'],
                correct_answer: 1,
                explanation: 'useState is the primary hook for managing local component state',
              },
            ],
          },
        },
      ];

      const mockProgress: SessionProgress = {
        session_id: mockSession.session_id,
        current_content_index: 0,
        completion_percentage: 0,
        time_spent_minutes: 0,
        concepts_mastered: [],
        knowledge_gaps: [],
        engagement_score: 0.8,
      };

      setSessionData(mockSession);
      setContentItems(mockContent);
      setProgress(mockProgress);
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startSessionTimer = () => {
    timerRef.current = setInterval(() => {
      setSessionTimer(prev => prev + 1);
    }, 60000); // Update every minute
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    // In real implementation, control media playback
  };

  const handleNextContent = () => {
    if (currentContentIndex < contentItems.length - 1) {
      setCurrentContentIndex(currentContentIndex + 1);
      updateProgress(currentContentIndex + 1);
    }
  };

  const handlePreviousContent = () => {
    if (currentContentIndex > 0) {
      setCurrentContentIndex(currentContentIndex - 1);
      updateProgress(currentContentIndex - 1);
    }
  };

  const updateProgress = (contentIndex: number) => {
    if (progress) {
      const completionPercentage = ((contentIndex + 1) / contentItems.length) * 100;
      setProgress({
        ...progress,
        current_content_index: contentIndex,
        completion_percentage: completionPercentage,
        time_spent_minutes: sessionTimer,
      });
    }
  };

  const handleMarkComplete = () => {
    if (progress) {
      const updatedProgress = {
        ...progress,
        concepts_mastered: [...progress.concepts_mastered, currentContent.title],
        completion_percentage: Math.min(progress.completion_percentage + 100 / contentItems.length, 100),
      };
      setProgress(updatedProgress);

      // Auto-advance to next content
      setTimeout(() => {
        handleNextContent();
      }, 1000);
    }
  };

  const handleSelfAssessmentSubmit = () => {
    // In real implementation, save assessment data
    console.log('Self-assessment submitted:', selfAssessment);
    handleNextContent();
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      contentRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <PlayFilled />;
      case 'interactive':
        return <Settings />;
      case 'quiz':
        return <Help />;
      case 'text':
        return <InformationFilled />;
      default:
        return <InformationFilled />;
    }
  };

  const currentContent = contentItems[currentContentIndex];

  if (isLoading) {
    return (
      <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading withOverlay={false} />
      </div>
    );
  }

  if (!currentContent) {
    return (
      <InlineNotification
        kind="error"
        title="Content not found"
        subtitle="Unable to load learning content"
        style={{ margin: '1rem' }}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--cds-layer-01)' }}>
      {/* Header */}
      <div
        style={{
          backgroundColor: 'var(--cds-layer-02)',
          padding: '1rem 2rem',
          borderBottom: '1px solid var(--cds-border-subtle-01)',
        }}
      >
        <Grid>
          <Column lg={16}>
            <Breadcrumb style={{ marginBottom: '1rem', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
              <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
              <BreadcrumbItem href="/individual-learning">Individual Learning</BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>Learning Session</BreadcrumbItem>
            </Breadcrumb>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                    color: 'var(--cds-text-primary)',
                    marginBottom: '0.5rem',
                  }}
                >
                  {currentContent.title}
                </h1>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Tag type="blue">{currentContent.content_type}</Tag>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color: 'var(--cds-text-secondary)',
                    }}
                  >
                    <TimeFilled size={16} />
                    {formatTime(sessionTimer)} elapsed
                  </span>
                  <span
                    style={{
                      color: 'var(--cds-text-secondary)',
                    }}
                  >
                    {Math.round(progress?.completion_percentage || 0)}% complete
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button kind="ghost" renderIcon={Help} onClick={() => setShowHelp(true)}>
                  Help
                </Button>
                <Button kind="ghost" renderIcon={Settings} onClick={() => setShowSettings(true)}>
                  Settings
                </Button>
                <Button kind="ghost" renderIcon={isFullscreen ? Minimize : Maximize} onClick={toggleFullscreen}>
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
              </div>
            </div>

            {/* Progress Indicator */}
            <div style={{ marginTop: '1.5rem' }}>
              <ProgressIndicator currentIndex={currentContentIndex}>
                {contentItems.map((item, index) => (
                  <ProgressStep
                    key={item.content_id}
                    complete={index < currentContentIndex}
                    current={index === currentContentIndex}
                    label={item.title}
                    description={item.content_type}
                  />
                ))}
              </ProgressIndicator>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Main Content Area */}
      <div ref={contentRef} style={{ padding: '2rem 1rem', maxWidth: '100vw', boxSizing: 'border-box' }}>
        <Grid>
          <Column lg={12} md={6} sm={4}>
            {/* Content Display */}
            <Tile
              style={{
                backgroundColor: 'var(--cds-layer-02)',
                minHeight: '400px',
                marginBottom: '1rem',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem',
                }}
              >
                {getContentIcon(currentContent.content_type)}
                <h2
                  style={{
                    fontSize: 'var(--cds-productive-heading-03-font-size)',
                    color: 'var(--cds-text-primary)',
                  }}
                >
                  {currentContent.title}
                </h2>
              </div>

              <p
                style={{
                  color: 'var(--cds-text-secondary)',
                  marginBottom: '1.5rem',
                }}
              >
                {currentContent.description}
              </p>

              {/* Content based on type */}
              {currentContent.content_type === 'video' && (
                <div
                  style={{
                    backgroundColor: 'var(--cds-layer-01)',
                    height: '300px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <PlayFilled size={48} style={{ color: 'var(--cds-icon-primary)', marginBottom: '1rem' }} />
                    <p style={{ color: 'var(--cds-text-secondary)' }}>Video content would play here</p>
                    <Button
                      kind="primary"
                      renderIcon={PlayFilled}
                      onClick={handlePlayPause}
                      style={{ marginTop: '1rem' }}
                    >
                      {isPlaying ? 'Pause' : 'Play'} Video
                    </Button>
                  </div>
                </div>
              )}

              {currentContent.content_type === 'interactive' && (
                <div
                  style={{
                    backgroundColor: 'var(--cds-layer-01)',
                    padding: '1.5rem',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                  }}
                >
                  <h3
                    style={{
                      color: 'var(--cds-text-primary)',
                      marginBottom: '1rem',
                    }}
                  >
                    Coding Exercise
                  </h3>
                  <TextArea
                    labelText="Code Editor"
                    placeholder="Write your React component here..."
                    rows={10}
                    value={currentContent.content_data.starter_code}
                    style={{ marginBottom: '1rem' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button kind="primary">Run Code</Button>
                    <Button kind="secondary">Get Hint</Button>
                    <Button kind="ghost">Reset</Button>
                  </div>
                </div>
              )}

              {currentContent.content_type === 'quiz' && (
                <div
                  style={{
                    backgroundColor: 'var(--cds-layer-01)',
                    padding: '1.5rem',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                  }}
                >
                  {currentContent.content_data.questions.map((question: any, index: number) => (
                    <div key={index} style={{ marginBottom: '2rem' }}>
                      <h3
                        style={{
                          color: 'var(--cds-text-primary)',
                          marginBottom: '1rem',
                        }}
                      >
                        Question {index + 1}: {question.question}
                      </h3>
                      <RadioButtonGroup name={`question-${index}`} orientation="vertical">
                        {question.options.map((option: string, optionIndex: number) => (
                          <RadioButton
                            key={optionIndex}
                            id={`q${index}-option${optionIndex}`}
                            labelText={option}
                            value={optionIndex.toString()}
                          />
                        ))}
                      </RadioButtonGroup>
                    </div>
                  ))}
                  <Button kind="primary">Submit Answers</Button>
                </div>
              )}

              {currentContent.content_type === 'text' && (
                <div
                  style={{
                    backgroundColor: 'var(--cds-layer-01)',
                    padding: '1.5rem',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                    fontSize:
                      adaptiveSettings.accessibility_settings.font_size === 'large'
                        ? '1.125rem'
                        : adaptiveSettings.accessibility_settings.font_size === 'small'
                          ? '0.875rem'
                          : '1rem',
                  }}
                >
                  <p style={{ color: 'var(--cds-text-primary)', lineHeight: 1.6 }}>
                    This would contain the text-based learning content with proper formatting, code examples, and
                    interactive elements embedded within the text.
                  </p>
                </div>
              )}

              {/* Content Controls */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    kind="secondary"
                    renderIcon={SkipBackFilled}
                    onClick={handlePreviousContent}
                    disabled={currentContentIndex === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    kind="secondary"
                    renderIcon={SkipForwardFilled}
                    onClick={handleNextContent}
                    disabled={currentContentIndex === contentItems.length - 1}
                  >
                    Next
                  </Button>
                </div>

                <Button kind="primary" renderIcon={CheckmarkFilled} onClick={handleMarkComplete}>
                  Mark Complete
                </Button>
              </div>
            </Tile>
          </Column>

          <Column lg={4} md={2} sm={4}>
            {/* Learning Objectives */}
            <Tile
              style={{
                backgroundColor: 'var(--cds-layer-02)',
                marginBottom: '1rem',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--cds-productive-heading-02-font-size)',
                  color: 'var(--cds-text-primary)',
                  marginBottom: '1rem',
                }}
              >
                Learning Objectives
              </h3>
              {currentContent.learning_objectives.map((objective, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                  }}
                >
                  <CheckmarkFilled
                    size={16}
                    style={{
                      color: 'var(--cds-support-success)',
                      marginTop: '0.125rem',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      color: 'var(--cds-text-primary)',
                      fontSize: 'var(--cds-body-compact-01-font-size)',
                    }}
                  >
                    {objective}
                  </span>
                </div>
              ))}
            </Tile>

            {/* Self-Assessment */}
            <Tile
              style={{
                backgroundColor: 'var(--cds-layer-02)',
                marginBottom: '1rem',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--cds-productive-heading-02-font-size)',
                  color: 'var(--cds-text-primary)',
                  marginBottom: '1rem',
                }}
              >
                Self-Assessment
              </h3>

              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: 'var(--cds-body-compact-01-font-size)',
                    color: 'var(--cds-text-primary)',
                  }}
                >
                  Difficulty Level (1-10)
                </label>
                <Slider
                  value={selfAssessment.difficulty_rating}
                  min={1}
                  max={10}
                  step={1}
                  onChange={value => setSelfAssessment(prev => ({ ...prev, difficulty_rating: value.value }))}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: 'var(--cds-body-compact-01-font-size)',
                    color: 'var(--cds-text-primary)',
                  }}
                >
                  Confidence Level (1-10)
                </label>
                <Slider
                  value={selfAssessment.confidence_level}
                  min={1}
                  max={10}
                  step={1}
                  onChange={value => setSelfAssessment(prev => ({ ...prev, confidence_level: value.value }))}
                />
              </div>

              <Button kind="secondary" onClick={handleSelfAssessmentSubmit} style={{ width: '100%' }}>
                Submit Assessment
              </Button>
            </Tile>

            {/* Progress Summary */}
            <Tile
              style={{
                backgroundColor: 'var(--cds-layer-02)',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--cds-productive-heading-02-font-size)',
                  color: 'var(--cds-text-primary)',
                  marginBottom: '1rem',
                }}
              >
                Session Progress
              </h3>

              <div style={{ marginBottom: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                  }}
                >
                  <span style={{ color: 'var(--cds-text-primary)' }}>Completion</span>
                  <span style={{ color: 'var(--cds-text-primary)' }}>
                    {Math.round(progress?.completion_percentage || 0)}%
                  </span>
                </div>
                <div
                  style={{
                    height: '8px',
                    backgroundColor: 'var(--cds-layer-01)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progress?.completion_percentage || 0}%`,
                      backgroundColor: 'var(--cds-support-success)',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              <StructuredListWrapper>
                <StructuredListBody>
                  <StructuredListRow>
                    <StructuredListCell head>Time Spent</StructuredListCell>
                    <StructuredListCell>{formatTime(sessionTimer)}</StructuredListCell>
                  </StructuredListRow>
                  <StructuredListRow>
                    <StructuredListCell head>Content Items</StructuredListCell>
                    <StructuredListCell>
                      {currentContentIndex + 1} / {contentItems.length}
                    </StructuredListCell>
                  </StructuredListRow>
                  <StructuredListRow>
                    <StructuredListCell head>Concepts Mastered</StructuredListCell>
                    <StructuredListCell>{progress?.concepts_mastered.length || 0}</StructuredListCell>
                  </StructuredListRow>
                </StructuredListBody>
              </StructuredListWrapper>
            </Tile>
          </Column>
        </Grid>
      </div>

      {/* Settings Modal */}
      <Modal
        open={showSettings}
        onRequestClose={() => setShowSettings(false)}
        modalHeading="Learning Preferences"
        primaryButtonText="Save Settings"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => setShowSettings(false)}
      >
        <div style={{ marginBottom: '1rem' }}>
          <h4
            style={{
              color: 'var(--cds-text-primary)',
              marginBottom: '1rem',
            }}
          >
            Accessibility Settings
          </h4>

          <div style={{ marginBottom: '1rem', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <Toggle
              id="high-contrast"
              labelText="High Contrast Mode"
              toggled={adaptiveSettings.accessibility_settings.high_contrast}
              onToggle={checked =>
                setAdaptiveSettings(prev => ({
                  ...prev,
                  accessibility_settings: { ...prev.accessibility_settings, high_contrast: checked },
                }))
              }
            />
          </div>

          <div style={{ marginBottom: '1rem', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <Toggle
              id="closed-captions"
              labelText="Closed Captions"
              toggled={adaptiveSettings.accessibility_settings.closed_captions}
              onToggle={checked =>
                setAdaptiveSettings(prev => ({
                  ...prev,
                  accessibility_settings: { ...prev.accessibility_settings, closed_captions: checked },
                }))
              }
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--cds-body-compact-01-font-size)',
                color: 'var(--cds-text-primary)',
              }}
            >
              Font Size
            </label>
            <ContentSwitcher
              onChange={name =>
                setAdaptiveSettings(prev => ({
                  ...prev,
                  accessibility_settings: { ...prev.accessibility_settings, font_size: name.name as any },
                }))
              }
              style={{ minHeight: '44px' }}
            >
              <Switch name="small" text="Small" />
              <Switch name="medium" text="Medium" />
              <Switch name="large" text="Large" />
            </ContentSwitcher>
          </div>
        </div>
      </Modal>

      {/* Help Modal */}
      <Modal
        open={showHelp}
        onRequestClose={() => setShowHelp(false)}
        modalHeading="Learning Session Help"
        primaryButtonText="Got it"
        onRequestSubmit={() => setShowHelp(false)}
      >
        <div
          style={
            {
              '--cds-accordion-item-height': '44px',
            } as React.CSSProperties
          }
        >
          <Accordion>
            <AccordionItem title="Navigation Controls">
              <p>
                Use the Previous/Next buttons to navigate between content items. You can also click on the progress
                indicator to jump to specific content.
              </p>
            </AccordionItem>
            <AccordionItem title="Self-Assessment">
              <p>
                Rate the difficulty and your confidence level for each content item. This helps our adaptive algorithm
                personalize your learning experience.
              </p>
            </AccordionItem>
            <AccordionItem title="Progress Tracking">
              <p>
                Your progress is automatically saved. You can pause and resume your session at any time without losing
                your place.
              </p>
            </AccordionItem>
            <AccordionItem title="Accessibility Features">
              <p>
                Access the settings menu to adjust font size, enable high contrast mode, or turn on closed captions for
                video content.
              </p>
            </AccordionItem>
          </Accordion>
        </div>
      </Modal>
    </div>
  );
};

export default IndividualLearningSession;
